import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { useT } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Users, Phone, Mail, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import EditEntityDialog from '../components/EditEntityDialog';
import RowActions from '../components/RowActions';
import TagsInput from '../components/TagsInput';

const MANAGE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR'];

export default function Staff() {
  const t = useT();
  const { user } = useContext(AuthContext);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newStaff, setNewStaff] = useState({ name: '', employee_type: 'technician', specialization: '', phone: '', email: '', tags: [] });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/staff`, { withCredentials: true });
      setStaff(r.data);
    } catch { toast.error('Failed to load staff'); }
    setLoading(false);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/staff`, newStaff, { withCredentials: true });
      toast.success('Staff added');
      setShowNew(false);
      setNewStaff({ name: '', employee_type: 'technician', specialization: '', phone: '', email: '', tags: [] });
      fetchData();
    } catch { toast.error('Failed to add staff'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this staff member?')) return;
    try {
      await axios.delete(`${API}/staff/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <Layout>
      <div data-testid="staff-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('staff.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('staff.subtitle')}</p>
            <p className="text-sm text-muted-foreground">Technicians, cleaners and coordinators</p>
          </div>
          {MANAGE.includes(user?.role) && (
            <Dialog open={showNew} onOpenChange={setShowNew}>
              <DialogTrigger asChild>
                <Button data-testid="new-staff-btn" className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1" />Add Staff</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add Staff Member</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div><Label>Name</Label><Input data-testid="staff-name-input" value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} /></div>
                  <div><Label>Type</Label>
                    <Select value={newStaff.employee_type} onValueChange={(v) => setNewStaff({ ...newStaff, employee_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="technician">Technician</SelectItem>
                        <SelectItem value="cleaner">Cleaner</SelectItem>
                        <SelectItem value="coordinator">Coordinator</SelectItem>
                        <SelectItem value="security">Security</SelectItem>
                        <SelectItem value="engineer">Engineer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Specialization</Label><Input value={newStaff.specialization} onChange={(e) => setNewStaff({ ...newStaff, specialization: e.target.value })} placeholder="e.g., HVAC, Electrical" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Phone</Label><Input value={newStaff.phone} onChange={(e) => setNewStaff({ ...newStaff, phone: e.target.value })} /></div>
                    <div><Label>Email</Label><Input type="email" value={newStaff.email} onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })} /></div>
                  </div>
                  <div><Label>Tags</Label><TagsInput value={newStaff.tags} onChange={(tags) => setNewStaff({ ...newStaff, tags })} testIdPrefix="staff-tag" /></div>
                  <Button data-testid="submit-staff-btn" onClick={handleCreate} disabled={!newStaff.name} className="w-full bg-teal-600 hover:bg-teal-700">Add Staff</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading...</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {staff.map((s) => (
              <Card key={s.id} data-testid={`staff-card-${s.id}`} className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-bold">{s.name?.charAt(0)}</div>
                    <div>
                      <h3 className="font-semibold text-foreground">{s.name}</h3>
                      <p className="text-xs text-muted-foreground capitalize">{s.employee_type}</p>
                    </div>
                  </div>
                  {MANAGE.includes(user?.role) && (
                    <RowActions
                      testIdPrefix={`staff-${s.id}`}
                      onEdit={() => setEditingId(s.id)}
                      onDelete={() => handleDelete(s.id)}
                      deleteLabel={`staff member "${s.name}"`}
                    />
                  )}
                </div>
                <div className="space-y-1.5 text-sm text-muted-foreground">
                  {s.specialization && <div className="flex items-center gap-2"><Briefcase className="w-3.5 h-3.5" />{s.specialization}</div>}
                  {s.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" />{s.phone}</div>}
                  {s.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" />{s.email}</div>}
                </div>
                <span className={`inline-block mt-3 px-2 py-0.5 rounded-full text-xs font-semibold ${s.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-700'}`}>{s.status}</span>
                {(s.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {s.tags.map((tg, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">{tg}</span>)}
                  </div>
                )}
              </Card>
            ))}
            {staff.length === 0 && (
              <Card className="p-10 text-center col-span-full">
                <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-muted-foreground">No staff yet</p>
              </Card>
            )}
          </div>
        )}

        {editingId && (
          <EditEntityDialog
            open
            hideTrigger
            onOpenChange={(v) => { if (!v) setEditingId(null); }}
            item={staff.find(x => x.id === editingId)}
            endpoint="staff"
            title="Edit Staff"
            onSaved={() => { setEditingId(null); fetchData(); }}
            fields={[
              { key: 'name', label: 'Name' },
              { key: 'employee_type', label: 'Employee Type', type: 'select', options: [
                { value: 'technician', label: 'Technician' },
                { value: 'supervisor', label: 'Supervisor' },
                { value: 'cleaner', label: 'Cleaner' },
                { value: 'security', label: 'Security' },
                { value: 'engineer', label: 'Engineer' },
                { value: 'manager', label: 'Manager' },
                { value: 'storekeeper', label: 'Storekeeper' },
                { value: 'other', label: 'Other' },
              ]},
              { key: 'specialization', label: 'Specialization' },
              { key: 'phone', label: 'Phone', type: 'tel' },
              { key: 'email', label: 'Email', type: 'email' },
            ]}
          />
        )}
      </div>
    </Layout>
  );
}
